import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/api";
import {
  login as loginRequest,
  logout as logoutRequest,
  register as registerRequest,
} from "../api/auth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [session, setSessionState] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const applySession = (nextSession, persistToken = true) => {
    setSessionState(nextSession);
    setToken(nextSession?.access_token ?? null);

    if (persistToken && nextSession?.access_token) {
      localStorage.setItem("access_token", nextSession.access_token);
    } else if (persistToken) {
      localStorage.removeItem("access_token");
    }
  };

  useEffect(() => {
    const fetchSession = async () => {
      const storedToken = localStorage.getItem("access_token");

      if (!storedToken) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await apiClient.get("/api/auth/me", {
          headers: { Authorization: `Bearer ${storedToken}` },
        });

        applySession(
          { access_token: storedToken, user: data?.user ?? null },
          false,
        );
      } catch (error) {
        localStorage.removeItem("access_token");
        setSessionState(null);
        setToken(null);
      }
      setLoading(false);
    };

    fetchSession();
  }, []);

  const login = async (email, password) => {
    const data = await loginRequest(email, password);
    if (!data) throw new Error("Login failed. Please try again.");

    if (data.access_token) {
      applySession({
        access_token: data.access_token,
        user: data.user ?? null,
      });
    }

    return data;
  };

  const register = async (payload) => {
    const data = await registerRequest(payload);
    if (!data) throw new Error("Registration failed. Please try again.");

    if (data.access_token) {
      applySession({
        access_token: data.access_token,
        user: data.user ?? null,
      });
    }

    return data;
  };

  const loginWithProvider = async (provider) => {
    throw new Error(`OAuth login for ${provider} is not configured.`);
  };

  const logout = async () => {
    logoutRequest();
    applySession(null);
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (session?.access_token) {
      localStorage.setItem("access_token", session.access_token);
    } else {
      localStorage.removeItem("access_token");
    }
  }, [session]);

  return (
    <AuthContext.Provider
      value={{
        session,
        token,
        user: session?.user,
        loading,
        setSession: applySession,
        login,
        register,
        loginWithProvider,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
